import { useState, useEffect } from "react";

import {
  Box,
  Grid,
  Typography,
  Button,
  Chip,
  Checkbox,
  CircularProgress,
  Alert,
  useTheme
} from "@mui/material";
import MergeIcon from "@mui/icons-material/Merge";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { DefaultPaper } from "@aclymatepackages/atoms";

import { fetchOurApi } from "../../../helpers/utils/apiCalls";

const ScoreChip = ({ label, value, goodThreshold }) => {
  const { palette } = useTheme();
  const color =
    value >= goodThreshold ? palette.secondary.main : palette.warning.main;

  return (
    <Chip
      size="small"
      label={`${label}: ${Math.round(value * 100)}%`}
      style={{ backgroundColor: color, color: "white" }}
    />
  );
};

// Older candidates (created before this field existed) have neither count --
// shown as "exposure unknown" rather than 0, since 0 is a real, meaningful
// value (no active vendor-relationships docs at all) that must not be
// confused with "never computed."
const ExposureChip = ({ vendorARelationshipCount, vendorBRelationshipCount }) => {
  const { palette } = useTheme();
  const hasExposureData =
    typeof vendorARelationshipCount === "number" &&
    typeof vendorBRelationshipCount === "number";

  if (!hasExposureData) {
    return (
      <Chip
        size="small"
        variant="outlined"
        label="Exposure: unknown"
        style={{ color: palette.text.secondary }}
      />
    );
  }

  const totalExposure = vendorARelationshipCount + vendorBRelationshipCount;

  return (
    <Chip
      size="small"
      label={`Exposure: ${totalExposure} relationship${totalExposure === 1 ? "" : "s"}`}
      style={{
        backgroundColor: totalExposure > 0 ? palette.primary.main : palette.backgroundGray.dark,
        color: totalExposure > 0 ? palette.common.white : palette.text.secondary
      }}
    />
  );
};

const CandidateCard = ({ candidate, onMerge, onDismiss }) => {
  const {
    id,
    vendorAId,
    vendorAName,
    vendorBId,
    vendorBName,
    fuzzyScore,
    llmConfidence,
    recommendedPrimaryId,
    vendorARelationshipCount,
    vendorBRelationshipCount
  } = candidate;

  const { palette } = useTheme();

  const [primaryId, setPrimaryId] = useState(recommendedPrimaryId);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [dismissLoading, setDismissLoading] = useState(false);
  const [mergeError, setMergeError] = useState(null);

  const secondaryId = primaryId === vendorAId ? vendorBId : vendorAId;
  const primaryName = primaryId === vendorAId ? vendorAName : vendorBName;
  const secondaryName = primaryId === vendorAId ? vendorBName : vendorAName;

  const handleMerge = async () => {
    setMergeLoading(true);
    setMergeError(null);
    try {
      // onMerge throws on a blocked (needsReview) or otherwise unsuccessful
      // response -- see its own comment for why this must never be treated
      // as a silent success.
      //
      // acknowledgedIndustryDifference: true only here, never in onBulkMerge
      // below -- this is the one path where an admin has actually looked at
      // this specific candidate's primary/secondary names and clicked a
      // per-candidate Merge button, so it's the only caller allowed to waive
      // the backend's industry-mismatch guard (mergeGlobalVendors /
      // executeVendorMerge in controllers/vendors.js). The bulk-merge button
      // below must still hit that guard unacknowledged, since it's an
      // unattended batch action with no per-candidate human review.
      await onMerge({
        primaryVendorId: primaryId,
        secondaryVendorId: secondaryId,
        candidateId: id,
        acknowledgedIndustryDifference: true
      });
    } catch (err) {
      setMergeError(err.message || "Merge failed.");
    } finally {
      setMergeLoading(false);
    }
  };

  const handleDismiss = async () => {
    setDismissLoading(true);
    await onDismiss(id);
    setDismissLoading(false);
  };

  return (
    <DefaultPaper>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={5}>
          <Grid container direction="column" spacing={1}>
            <Grid item>
              <Typography variant="body2" color="textSecondary">
                Primary (will be kept)
              </Typography>
            </Grid>
            <Grid item>
              <Button
                variant={primaryId === vendorAId ? "contained" : "outlined"}
                size="small"
                onClick={() => setPrimaryId(vendorAId)}
                endIcon={
                  primaryId === vendorAId ? <CheckCircleIcon /> : undefined
                }
                fullWidth
              >
                {vendorAName}
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant={primaryId === vendorBId ? "contained" : "outlined"}
                size="small"
                onClick={() => setPrimaryId(vendorBId)}
                endIcon={
                  primaryId === vendorBId ? <CheckCircleIcon /> : undefined
                }
                fullWidth
              >
                {vendorBName}
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Grid container direction="column" spacing={1} alignItems="center">
            <Grid item>
              <ScoreChip label="Fuzzy" value={fuzzyScore} goodThreshold={0.9} />
            </Grid>
            <Grid item>
              <ScoreChip
                label="LLM"
                value={llmConfidence}
                goodThreshold={0.85}
              />
            </Grid>
            <Grid item>
              <ExposureChip
                vendorARelationshipCount={vendorARelationshipCount}
                vendorBRelationshipCount={vendorBRelationshipCount}
              />
            </Grid>
            <Grid item>
              <Typography
                variant="caption"
                color="textSecondary"
                align="center"
                display="block"
              >
                {primaryName} ← {secondaryName}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Grid container direction="column" spacing={1}>
            <Grid item>
              {mergeLoading ? (
                <CircularProgress size={24} />
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<MergeIcon />}
                  onClick={handleMerge}
                  fullWidth
                >
                  Merge
                </Button>
              )}
            </Grid>
            <Grid item>
              {dismissLoading ? (
                <CircularProgress size={24} />
              ) : (
                <Button
                  variant="outlined"
                  onClick={handleDismiss}
                  fullWidth
                  style={{ color: palette.text.secondary }}
                >
                  Dismiss
                </Button>
              )}
            </Grid>
            {mergeError && (
              <Grid item>
                <Alert severity="warning">{mergeError}</Alert>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </DefaultPaper>
  );
};

// Older candidates lack vendorA/BRelationshipCount (added 2026-09-02) --
// treated as lowest priority (0) for sorting purposes only, never displayed
// as 0 itself (ExposureChip shows "unknown" for those, see above).
const totalExposure = ({ vendorARelationshipCount, vendorBRelationshipCount }) =>
  (vendorARelationshipCount || 0) + (vendorBRelationshipCount || 0);

const sortByExposureDescending = (candidates) =>
  [...candidates].sort((a, b) => totalExposure(b) - totalExposure(a));

// A confirmed-identity threshold, not this page's own invention -- 0.9 is
// GLOBAL_FUZZY_CANDIDATE_THRESHOLD in vendorCreation.js/the scan script,
// the bar every candidate here already had to clear just to exist. Below
// this, treat it as a fuzzy match that still needs a human look, same as
// before.
const BULK_MERGE_CONFIDENCE_THRESHOLD = 0.9;

// Must track CANDIDATE_MATCH_VERSION in functions/utils/cronJobs/vendorDeduplication.js
// exactly -- the front-end can't import that backend module directly, so this
// is a deliberate, manually-kept-in-sync mirror. Bump both together.
const CANDIDATE_MATCH_VERSION = 3;

// The tier this page lets an admin bulk-merge without opening each card:
// either the docs share the exact same name (zero identity ambiguity) or
// the match cleared a high confirmed-identity bar (fuzzy AND LLM score both
// >= 0.9) -- AND, either way, neither vendor has a single vendor-relationships
// doc anywhere (zero accounting exposure -- nothing to get wrong). Matches
// the server-side admin bypass in controllers/vendors.js's mergeGlobalVendors,
// which enforces the zero-exposure condition itself rather than trusting
// this filter alone (that bypass doesn't care about name/confidence at all,
// only exposure -- this filter is what actually restricts the tier).
//
// industriesDiffer must veto both branches, not just the confidence one --
// two vendor docs can share the exact same name (e.g. two franchisees both
// stemmed to "Proforma Divine") while being classified in different NAICS
// industry groups. The exact-name branch doesn't look at llmConfidence at
// all, so capping confidence alone (vendorDeduplication.js) doesn't stop
// this tier from merging them; it has to be checked here directly.
//
// matchPromptVersion must also be checked directly, not inferred from
// industriesDiffer's absence -- vendorDeduplication.js only ever writes
// industriesDiffer when it's true (never an explicit `false`), so a
// candidate that hasn't been re-evaluated since CANDIDATE_MATCH_VERSION was
// bumped (the backlog refreshes in bounded weekly batches, so this window
// can be long) looks identical to one that was checked and confirmed to
// match -- both have no industriesDiffer field at all.
const isBulkMergeEligible = ({
  vendorAName,
  vendorBName,
  vendorARelationshipCount,
  vendorBRelationshipCount,
  fuzzyScore,
  llmConfidence,
  industriesDiffer,
  matchPromptVersion
}) =>
  vendorARelationshipCount === 0 &&
  vendorBRelationshipCount === 0 &&
  matchPromptVersion === CANDIDATE_MATCH_VERSION &&
  !industriesDiffer &&
  (vendorAName === vendorBName ||
    (fuzzyScore >= BULK_MERGE_CONFIDENCE_THRESHOLD &&
      llmConfidence >= BULK_MERGE_CONFIDENCE_THRESHOLD));

const secondaryVendorId = ({ vendorAId, vendorBId, recommendedPrimaryId }) =>
  recommendedPrimaryId === vendorAId ? vendorBId : vendorAId;

const AdminVendorNormalizationPage = () => {
  const [candidates, setCandidates] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  // Separate from `error` on purpose: `error` gates the full-page
  // <Alert>-only render below, replacing every already-loaded candidate
  // card. A transient failure on a later "Load more" page must stay
  // recoverable -- the admin keeps their review progress and can retry --
  // not lose the whole loaded queue over one flaky request.
  const [loadMoreError, setLoadMoreError] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkMerging, setBulkMerging] = useState(false);
  const [bulkMergeResult, setBulkMergeResult] = useState(null);

  const fetchPage = (cursor) =>
    fetchOurApi({
      path: "/vendors/get-vendor-merge-candidates",
      method: "POST",
      data: { cursor },
      callback: ({ candidates: page, hasMore: pageHasMore, nextCursor: cursorForNextPage } = {}) => {
        setCandidates((prev) => [...prev, ...(page || [])]);
        setHasMore(Boolean(pageHasMore));
        setNextCursor(cursorForNextPage || null);
      }
    });

  useEffect(() => {
    fetchPage(null)
      .then(() => setLoading(false))
      .catch(() => {
        setError("Failed to load merge candidates.");
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLoadMore = async () => {
    setLoadingMore(true);
    setLoadMoreError(null);
    try {
      await fetchPage(nextCursor);
    } catch {
      setLoadMoreError("Failed to load more merge candidates.");
    }
    setLoadingMore(false);
  };

  const onMerge = async (mergeParams) => {
    const result = await fetchOurApi({
      path: "/vendors/merge-global-vendors",
      method: "POST",
      data: mergeParams
    });
    // merge-global-vendors answers a blocked merge with a plain
    // {error:true, needsReview:true, ...} object, not an HTTP error status --
    // fetchOurApi resolves normally either way, so this must be checked
    // explicitly. Throwing here (rather than silently removing the
    // candidate below) is what makes CandidateCard's own catch and
    // onBulkMerge's existing try/catch both treat a blocked merge as "not
    // done" -- a blocked merge still needs a human, exactly the same as a
    // real failure, not something to report as complete.
    if (result?.error) {
      throw new Error(
        result.needsReview
          ? `Blocked: real impact is ${
              Number.isFinite(result.maxAbsPercentChange)
                ? `${result.maxAbsPercentChange.toFixed(1)}%`
                : "unresolvable (zero-denominator buyer-year)"
            } for at least one buyer-year -- ${result.message || "needs review"}`
          : result.message || "Merge failed."
      );
    }
    setCandidates((prev) =>
      prev.filter(({ id }) => id !== mergeParams.candidateId)
    );
  };

  const toggleSelected = (candidateId) => {
    setSelectedIds((prev) => {
      const nextIds = prev.has(candidateId)
        ? [...prev].filter((id) => id !== candidateId)
        : [...prev, candidateId];
      return new Set(nextIds);
    });
  };

  const eligibleLoadedIds = candidates.filter(isBulkMergeEligible).map(({ id }) => id);
  const allEligibleSelected =
    eligibleLoadedIds.length > 0 &&
    eligibleLoadedIds.every((id) => selectedIds.has(id));

  const toggleSelectAllEligible = () => {
    setSelectedIds(allEligibleSelected ? new Set() : new Set(eligibleLoadedIds));
  };

  // Sequential (reduce over a promise chain), not Promise.all -- each
  // merge is a real production write, and running them one at a time keeps
  // a single failure isolated to its own candidate instead of firing every
  // selected merge concurrently against the backend at once.
  const onBulkMerge = async () => {
    setBulkMerging(true);
    setBulkMergeResult(null);

    const idsToMerge = [...selectedIds];
    const results = await idsToMerge.reduce(async (accPromise, candidateId) => {
      const acc = await accPromise;
      const candidate = candidates.find(({ id }) => id === candidateId);
      if (!candidate) {
        return acc;
      }

      try {
        await onMerge({
          primaryVendorId: candidate.recommendedPrimaryId,
          secondaryVendorId: secondaryVendorId(candidate),
          candidateId: candidate.id
        });
        return { ...acc, succeeded: [...acc.succeeded, candidateId] };
      } catch {
        return { ...acc, failed: [...acc.failed, candidateId] };
      }
    }, Promise.resolve({ succeeded: [], failed: [] }));

    setSelectedIds(new Set());
    setBulkMergeResult(results);
    setBulkMerging(false);
  };

  const onDismiss = async (candidateId) => {
    await fetchOurApi({
      path: "/vendors/dismiss-vendor-merge-candidate",
      method: "POST",
      data: { candidateId }
    });
    setCandidates((prev) => prev.filter(({ id }) => id !== candidateId));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Vendor Normalization
      </Typography>
      <Typography variant="body1" color="textSecondary" gutterBottom>
        These vendor pairs were identified as likely duplicates in the global
        vendor database. Select the primary vendor to keep and merge the other
        into it. Sorted by accounting exposure (active vendor-relationships
        across both docs), highest first.
      </Typography>
      {(eligibleLoadedIds.length > 0 || bulkMergeResult) && (
        <Box mb={2}>
          {eligibleLoadedIds.length > 0 && (
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <Typography variant="body2" color="textSecondary">
                {eligibleLoadedIds.length} loaded candidate
                {eligibleLoadedIds.length === 1 ? " is" : "s are"} an exact or
                high-confidence (≥90%) name match and{" "}
                {eligibleLoadedIds.length === 1 ? "has" : "have"} zero
                accounting exposure on either side -- safe to bulk-merge with
                no per-case review.
              </Typography>
              <Button size="small" onClick={toggleSelectAllEligible}>
                {allEligibleSelected ? "Deselect all" : "Select all eligible"}
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={selectedIds.size === 0 || bulkMerging}
                onClick={onBulkMerge}
                startIcon={bulkMerging ? undefined : <MergeIcon />}
              >
                {bulkMerging ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  `Merge ${selectedIds.size} selected`
                )}
              </Button>
            </Box>
          )}
          {bulkMergeResult && (
            <Alert
              severity={bulkMergeResult.failed.length ? "warning" : "success"}
              sx={{ mt: 1 }}
            >
              {bulkMergeResult.succeeded.length} merged
              {bulkMergeResult.failed.length
                ? `, ${bulkMergeResult.failed.length} failed -- still shown below for individual review`
                : "."}
            </Alert>
          )}
        </Box>
      )}
      {!candidates.length && !hasMore ? (
        <Box mt={4}>
          <Alert severity="success">
            No duplicate vendor candidates pending review.
          </Alert>
        </Box>
      ) : (
        // Gated on !candidates.length && !hasMore, not !candidates.length
        // alone -- merging or dismissing every candidate on the current
        // page while more pages remain on the server must not fall into
        // the branch above: that would hide this grid (and the Load more
        // control inside it) and falsely tell the admin nothing is
        // pending, when the remaining server-side pages are still there
        // and simply unreachable through the UI at that point.
        <Grid container direction="column" spacing={2} mt={1}>
          {!candidates.length && (
            <Grid item>
              <Alert severity="info">
                All candidates on this page were merged or dismissed. Load
                more to continue reviewing.
              </Alert>
            </Grid>
          )}
          {sortByExposureDescending(candidates).map((candidate) => (
            <Grid item key={candidate.id}>
              <Box display="flex" alignItems="flex-start" gap={1}>
                {isBulkMergeEligible(candidate) && (
                  <Checkbox
                    checked={selectedIds.has(candidate.id)}
                    onChange={() => toggleSelected(candidate.id)}
                    sx={{ mt: 1 }}
                  />
                )}
                <Box flex={1}>
                  <CandidateCard
                    candidate={candidate}
                    onMerge={onMerge}
                    onDismiss={onDismiss}
                  />
                </Box>
              </Box>
            </Grid>
          ))}
          {hasMore && (
            <Grid item>
              <Box display="flex" flexDirection="column" alignItems="center" mt={2}>
                {loadMoreError && (
                  <Alert severity="error" sx={{ mb: 1, width: "100%" }}>
                    {loadMoreError}
                  </Alert>
                )}
                {loadingMore ? (
                  <CircularProgress size={24} />
                ) : (
                  <Button variant="outlined" onClick={onLoadMore}>
                    {loadMoreError ? "Retry" : "Load more"}
                  </Button>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default AdminVendorNormalizationPage;
